import { Backup } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useMemo, useRef, useState } from 'react';

import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { VNBaseModal } from '@/modals/VNBaseModal';
import type { Geoblock } from '@/models/Geoblock';

import { EFFECTS, EMPTY_GEOBLOCK, GEOGRAPHY_LEVELS } from './GeoblockConstants';

type Props = {
  onClose: () => void;
  onImport: (data: Geoblock) => void;
};

const parseCsvContent = (content: string): Geoblock => {
  const lines = content.split('\n').filter((l) => l.trim().length > 0);
  const result = JSON.parse(JSON.stringify(EMPTY_GEOBLOCK)) as Geoblock;

  lines.forEach((line, index) => {
    // Skip header row
    if (index === 0 && line.toLowerCase().startsWith('type')) {
      return;
    }

    const parts = line
      .trim()
      .split(',')
      .map((p) => p.trim());
    if (parts.length < 3) {
      return;
    }

    const type = parts[0];
    const effect = parts[1];
    const value = parts.slice(2).join(',').trim();

    // translate Block And Allow to Deny and Allow
    const parsedEffect = Object.keys(EFFECTS).find(
      (key) => EFFECTS[key as keyof typeof EFFECTS].toLowerCase() === effect.toLowerCase()
    );
    // Normalize Zip/PostalCode to PostalCode
    const mappedType =
      type.toLowerCase() === 'zip/postalcode' ? 'PostalCode' : type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

    if (!Object.keys(GEOGRAPHY_LEVELS).includes(mappedType) || !Object.keys(EFFECTS).includes(parsedEffect) || !value) {
      return;
    }

    const rule = result.rules.find((r) => r.effect === parsedEffect && r.type === mappedType);
    if (rule) {
      rule.values.push(value);
    }
  });

  return result;
};

const processFile = (file: File, setError: (error: string) => void, setParsedData: (data: Geoblock | null) => void) => {
  setError('');

  const reader = new FileReader();
  reader.onload = (event) => {
    const content = event.target?.result as string;
    const data = parseCsvContent(content);
    if (Object.keys(data).length === 0) {
      setError('No valid rows found in CSV. Ensure the format is: type,effect,value');
      setParsedData(null);
    } else {
      setParsedData(data);
    }
  };
  reader.readAsText(file);
};

const GeoblockImportModal = ({ onClose, onImport }: Props) => {
  const [parsedData, setParsedData] = useState<Geoblock | null>(null);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    processFile(file, setError, setParsedData);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file, setError, setParsedData);
    }
  };

  const handleImport = () => {
    if (!parsedData) {
      return;
    }
    onImport(parsedData);
    onClose();
  };

  const totalValues = useMemo(() => parsedData?.rules.reduce((acc, rule) => acc + rule.values.length, 0) ?? 0, [parsedData]);

  return (
    <VNBaseModal open title="Import from CSV" width="sm" onClose={onClose}>
      <VnModalBody>
        <div className="geoblock-import-modal-content">
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />

          <div
            className={`import-dropzone${dragging ? ' drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={() => {
              return false;
            }}
            role="button"
            tabIndex={0}
          >
            <div className="import-dropzone-icon">
              <Backup fontSize="large" color="primary" />
            </div>
            <p className="import-dropzone-title">Drag and Drop File here</p>
            <p className="import-dropzone-text">or</p>
            <p className="import-dropzone-text">Choose file to upload</p>
            <Button
              variant="contained"
              size="small"
              className="import-browse-button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              BROWSE
            </Button>
          </div>

          <div className="import-help">
            Upload a CSV file with columns: Type, Effect, Value
            <br />
            Valid types: Country, Subdivision, DMA, City, Zip/PostalCode
            <br />
            Valid effects: Allow, Block
          </div>

          {error && <div className="import-error">{error}</div>}

          {parsedData && (
            <div className="import-preview">
              {totalValues} location{totalValues !== 1 ? 's' : ''} found in CSV
            </div>
          )}
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button variant="contained" className="cancel-button" onClick={onClose}>
              Cancel
            </Button>
            {parsedData && (
              <Button variant="contained" color="primary" onClick={handleImport}>
                Import
              </Button>
            )}
          </>
        }
      />
    </VNBaseModal>
  );
};

export default GeoblockImportModal;
